import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import ArrowRightAltRoundedIcon from '@mui/icons-material/ArrowRightAltRounded';

import { applicationsApi, type ApplicationChangeEntryDto } from '../../api/admin';
import {
  changeActionLabel,
  changeSourceWasUnreadable,
  groupChangesByChangeSet,
} from '../../lib/applicationFieldEditing';
import { ApplicationSectionCard } from './applicationSectionChrome';

function actionColor(
  action: ApplicationChangeEntryDto['action'],
): 'default' | 'warning' | 'info' | 'success' {
  switch (action) {
    case 'OVERRIDE_SET':
      return 'warning';
    case 'OVERRIDE_CLEAR':
      return 'info';
    case 'DECISION':
      return 'success';
    default:
      return 'default';
  }
}

/**
 * One changed field within a change-set.
 *
 * Shows before → after and the physical column, because "job title changed" is
 * less useful to an auditor than knowing it was `Employment.jobTitle` and that no
 * downstream row was written.
 */
function ChangeEntryRow({ entry }: { entry: ApplicationChangeEntryDto }) {
  const unreadable = changeSourceWasUnreadable(entry);

  return (
    <Box sx={{ py: 0.4 }}>
      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
        <Chip
          size="small"
          label={changeActionLabel(entry.action)}
          color={actionColor(entry.action)}
          variant="outlined"
          sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700 }}
        />
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {entry.fieldKey}
        </Typography>
        {entry.writeMode === 'OVERLAY' ? (
          <Tooltip
            title={`Stored as a correction against this application. ${entry.targetService} was not written.`}
            arrow
          >
            <Chip
              size="small"
              label="overlay"
              variant="outlined"
              sx={{ height: 18, fontSize: '0.62rem' }}
            />
          </Tooltip>
        ) : null}
      </Stack>

      <Stack
        direction="row"
        spacing={0.5}
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
        sx={{ mt: 0.15, pl: 0.25 }}
      >
        <Typography
          variant="body2"
          sx={{ color: 'text.secondary', textDecoration: 'line-through', wordBreak: 'break-word' }}
        >
          {entry.beforeValue ?? (unreadable ? 'unknown' : 'empty')}
        </Typography>
        <ArrowRightAltRoundedIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
        <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: 'break-word' }}>
          {entry.afterValue ?? 'empty'}
        </Typography>
      </Stack>

      {/* A blank before-value that actually means "the service was down" must not
          read as "the field was empty". */}
      {unreadable ? (
        <Typography variant="caption" color="warning.main" sx={{ display: 'block', pl: 0.25 }}>
          {unreadable}
        </Typography>
      ) : null}

      {/* The physical target — service, table, column, row id — is recorded on
          every change_log row and is what an engineer or an auditor traces later.
          It is storage detail, not something a reviewer reads, so it stays in the
          table and out of this panel. */}
    </Box>
  );
}

export interface ApplicationChangeHistoryPanelProps {
  applicationId: string;
  /** When set, only this field's history is shown. */
  fieldKey?: string | null;
  onClearFieldFilter?: () => void;
}

/**
 * Recorded admin changes, newest first, grouped into change-sets.
 *
 * Grouped because one reviewer act can touch several fields, and listing them
 * flat would make a single correction look like five.
 */
export function ApplicationChangeHistoryPanel({
  applicationId,
  fieldKey,
  onClearFieldFilter,
}: ApplicationChangeHistoryPanelProps) {
  const changesQ = useQuery({
    queryKey: ['applicationChanges', applicationId, fieldKey ?? null],
    queryFn: () =>
      applicationsApi.changes(applicationId, {
        limit: 100,
        ...(fieldKey ? { fieldKey } : {}),
      }),
    enabled: !!applicationId,
    retry: false,
  });

  const groups = useMemo(
    () => groupChangesByChangeSet(changesQ.data?.items ?? []),
    [changesQ.data?.items],
  );

  return (
    <ApplicationSectionCard
      title="Change history"
      subtitle={
        fieldKey
          ? `Filtered to ${fieldKey}`
          : 'Every recorded change by our staff, newest first'
      }
      icon={<HistoryRoundedIcon fontSize="small" />}
      action={
        fieldKey && onClearFieldFilter ? (
          <Button size="small" color="inherit" onClick={onClearFieldFilter}>
            Show all
          </Button>
        ) : undefined
      }
    >
      {changesQ.isPending ? (
        <Box sx={{ py: 2, textAlign: 'center' }}>
          <CircularProgress size={20} />
        </Box>
      ) : changesQ.isError ? (
        <Alert severity="error" variant="outlined">
          Could not load the change history.
        </Alert>
      ) : groups.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {fieldKey
            ? 'This field has not been changed.'
            : 'Nobody has changed anything on this application.'}
        </Typography>
      ) : (
        <Stack spacing={1.5} divider={<Divider flexItem />}>
          {groups.map((group) => (
            <Box key={group.changeSetId}>
              <Stack direction="row" spacing={0.75} alignItems="baseline" flexWrap="wrap" useFlexGap>
                <Typography variant="caption" sx={{ fontWeight: 700 }}>
                  {group.actor}
                </Typography>
                <Typography variant="caption" color="text.disabled">
                  {new Date(group.at).toLocaleString()}
                </Typography>
                <Chip
                  size="small"
                  variant="outlined"
                  label={group.reasonLabel}
                  sx={{ height: 18, fontSize: '0.65rem' }}
                />
                {group.entries.length > 1 ? (
                  <Typography variant="caption" color="text.disabled">
                    {group.entries.length} fields in one change
                  </Typography>
                ) : null}
              </Stack>

              <Typography
                variant="body2"
                sx={{ mt: 0.3, mb: 0.5, fontStyle: 'italic', color: 'text.secondary' }}
              >
                “{group.reasonDetail}”
              </Typography>

              <Stack spacing={0.4} sx={{ pl: 1, borderLeft: '2px solid', borderColor: 'divider' }}>
                {group.entries.map((entry) => (
                  <ChangeEntryRow key={entry.id} entry={entry} />
                ))}
              </Stack>
            </Box>
          ))}
        </Stack>
      )}
    </ApplicationSectionCard>
  );
}
